import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DataTable from "@/components/DataTable";
import HealthBadge, { drivingLogHealth } from "@/components/HealthBadge";
import DepartmentFilter, { useDepartmentFilter } from "@/components/DepartmentFilter";
import FirmFilter from "@/components/FirmFilter";
import EmployeeStatusFilter from "@/components/EmployeeStatusFilter";
import EmployeePicker from "@/components/EmployeePicker";
import { fmtDate } from "@/lib/format";
import { FileDown, Info } from "lucide-react";
import { toast } from "sonner";
import ModuleHint from "@/components/ModuleHint";

/**
 * Lenkprotokoll-Liste.
 *
 * Wichtig: Das Lenkprotokoll wird NICHT separat erfasst. Es wird automatisch aus
 * der Zeiterfassung abgeleitet — jeder Zeiteintrag mit Lenkbeginn + Lenkende
 * erscheint hier als Lenkprotokoll-Zeile. Diese Ansicht dient zur schnellen
 * Übersicht und für Einzel-PDF-Downloads. Bulk-/ZIP-Export läuft über das
 * Export-Center (Phase 5V).
 */
export default function Lenkprotokoll() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [deptFilter, setDeptFilter] = useDepartmentFilter("lenkprotokoll");
  const [empStatus, setEmpStatus] = useState("aktiv");
  const [empFilter, setEmpFilter] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (deptFilter) params.department_id = deptFilter;
      if (empStatus) params.employee_status = empStatus;
      if (empFilter) params.employee_id = empFilter;
      const { data } = await api.get("/time-entries", { params });
      // Nur Einträge mit Lenkbeginn UND Lenkende (= echte Lenkprotokoll-Zeilen)
      const filtered = (data || []).filter((t) => t.driving_start && t.driving_end);
      setItems(filtered);
    } finally { setLoading(false); }
  }
  useEffect(() => {
    api.get("/employees").then(({data}) => setEmployees(data));
    api.get("/vehicles").then(({data}) => setVehicles(data));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptFilter, empStatus, empFilter]);

  const empName = (id) => { const e = employees.find(x => x.id === id); return e ? `${e.first_name} ${e.last_name}` : "—"; };
  const vehPlate = (id) => { const v = vehicles.find(x => x.id === id); return v ? v.license_plate : "—"; };

  function _diffMin(a, b) {
    if (!a || !b) return 0;
    const [ha, ma] = a.split(":").map(Number);
    const [hb, mb] = b.split(":").map(Number);
    return Math.max(0, (hb * 60 + mb) - (ha * 60 + ma));
  }

  async function downloadPdf(t) {
    setDownloadingId(t.id);
    try {
      const res = await api.get(`/exports/lenkprotokoll/single/${t.id}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      const ln = (employees.find(e => e.id === t.employee_id)?.last_name) || "Lenkprotokoll";
      a.download = `Lenkprotokoll_${ln}_${t.date}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      toast.success("PDF heruntergeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download fehlgeschlagen");
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="p-6 md:p-8" data-testid="lenkprotokoll-page">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-zinc-100">Lenkprotokoll</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Automatisch aus der Zeiterfassung abgeleitet — jeder Eintrag mit Lenkbeginn + Lenkende</p>
        </div>
      </div>

      <ModuleHint moduleKey="lenkprotokoll" />

      <div className="mb-4 p-3 bg-blue-500/5 border border-blue-500/20 rounded-md flex items-start gap-2 text-xs text-zinc-300" data-testid="lenkprotokoll-info-box">
        <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
        <div>
          Das Lenkprotokoll wird <strong className="text-zinc-100">automatisch aus der Zeiterfassung</strong> erstellt.
          Damit ein Eintrag hier erscheint, muss in der Zeiterfassung sowohl <code className="text-blue-300">Lenkbeginn</code> als auch <code className="text-blue-300">Lenkende</code> eingetragen sein.
          Pro Zeile kann ein einzelnes A4-PDF schnell heruntergeladen werden. Sammel-/ZIP-Export läuft über das <strong className="text-zinc-100">Export-Center</strong>.
        </div>
      </div>

      <DataTable
        rows={items}
        loading={loading}
        searchableFields={[
          "workplace", "notes", "anmerkung", "date",
          (r) => empName(r.employee_id),
          (r) => vehPlate(r.vehicle_id),
        ]}
        searchPlaceholder="Fahrer, Kennzeichen, Einsatzort, Notiz..."
        testid="lenkprotokoll-table"
        emptyTitle="Keine Lenkprotokoll-Einträge"
        emptyHint="Es gibt noch keine Zeiterfassungs-Einträge mit Lenkbeginn und Lenkende."
        filters={
          <>
            <FirmFilter compact />
            <DepartmentFilter value={deptFilter} onChange={setDeptFilter} module="lenkprotokoll" testid="lenkprotokoll-dept-filter"/>
            <EmployeeStatusFilter value={empStatus} onChange={setEmpStatus} testid="lenkprotokoll-empstatus-filter"/>
            <div className="min-w-[200px]" data-testid="lenkprotokoll-emp-filter-wrap">
              <EmployeePicker
                value={empFilter}
                onChange={setEmpFilter}
                placeholder="Alle Fahrer"
                testid="lenkprotokoll-emp-filter"
                filter={(e) => e.role === "fahrer"}
              />
            </div>
          </>
        }
        columns={[
          { key: "_health", label: "",
            headerClassName: "w-6",
            render: (d) => {
              const h = drivingLogHealth(d);
              return <HealthBadge level={h.level} reasons={h.reasons} variant="dot" size="md" testid={`dl-health-${d.id}`} />;
            } },
          { key: "date", label: "Datum", sortable: true, render: (d) => <span className="font-mono text-xs text-zinc-300">{fmtDate(d.date)}</span> },
          { key: "_emp", label: "Fahrer", render: (d) => <span className="text-zinc-100">{empName(d.employee_id)}</span> },
          { key: "_veh", label: "Fahrzeug", render: (d) => <span className="font-mono text-xs text-zinc-400">{vehPlate(d.vehicle_id)}</span> },
          { key: "workplace", label: "Ort", sortable: true, render: (d) => <span className="text-zinc-300">{d.workplace || "—"}</span> },
          { key: "_period", label: "Lenkzeit", render: (d) => <span className="font-mono text-xs text-zinc-400">{d.driving_start} – {d.driving_end}</span> },
          { key: "_km", label: "KM",
            headerClassName: "text-right", className: "text-right font-mono tabular-nums text-zinc-300",
            render: (d) => (d.end_km||0) - (d.start_km||0) },
          { key: "_dauer", label: "Gesamt", sortable: true,
            headerClassName: "text-right", className: "text-right font-mono tabular-nums text-zinc-300",
            render: (d) => {
              const m = _diffMin(d.driving_start, d.driving_end);
              return `${Math.floor(m/60)}h ${m%60}m`;
            } },
          {
            key: "_actions", label: "",
            headerClassName: "text-right", className: "text-right",
            render: (d) => (
              <button
                onClick={(e) => { e.stopPropagation(); downloadPdf(d); }}
                disabled={downloadingId === d.id}
                title="Lenkprotokoll als PDF herunterladen"
                data-testid={`dl-pdf-${d.id}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-zinc-800 hover:bg-blue-600 hover:text-white border border-zinc-700 hover:border-blue-600 disabled:opacity-50 text-zinc-200 rounded transition-colors"
              >
                <FileDown className="w-3.5 h-3.5"/>
                {downloadingId === d.id ? "…" : "PDF"}
              </button>
            ),
          },
        ]}
      />
    </div>
  );
}
